import type { ToolConfig } from './types.js';

/**
 * Tool - Abstract Base Class (Template Method Pattern)
 * 
 * Defines the common workflow for all tools. Subclasses customize by overriding
 * hook methods rather than the main template method.
 * 
 * POODR Principles:
 * - Template Method Pattern: init() defines workflow, subclasses customize hooks
 * - Single Responsibility: Base class handles common UI behavior only
 * - Dependency Injection: config passed via constructor
 */

/** Generic type for DOM element mapping */
type ToolElements = Record<string, HTMLElement | null>;

abstract class Tool<TConfig extends ToolConfig = ToolConfig, TElements extends ToolElements = ToolElements> {
  protected config: TConfig;
  protected elements: TElements;
  protected isProcessing: boolean;
  protected currentFile: File | null;

  constructor(config: TConfig = {} as TConfig) {
    this.config = config;
    this.elements = this.bindElements();
    this.isProcessing = false;
    this.currentFile = null;
    
    // Template method - defines the workflow
    this.init();
  }

  /**
   * Template Method - defines initialization workflow
   * Do not override in subclasses
   */
  private init(): void {
    this.bindEvents();
    this.onInit();
  }

  /**
   * Hook Method - subclasses must implement
   * Bind DOM elements to this.elements
   */
  protected abstract bindElements(): TElements;

  /**
   * Hook Method - subclasses must implement
   * Bind event listeners
   */
  protected abstract bindEvents(): void;

  /**
   * Hook Method - subclasses may override
   * Called after init completes
   */
  protected onInit(): void {
    // Optional override
  }

  // ============================================
  // Shared UI Behavior (available to all tools)
  // ============================================

  protected readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  protected formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  protected isValidPdf(file: File): boolean {
    return file.type === 'application/pdf' || 
           file.name.toLowerCase().endsWith('.pdf');
  }

  protected downloadBlob(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  protected escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  protected animateProgress(progressElement: HTMLElement): number {
    let progress = 0;
    const interval = window.setInterval(() => {
      progress += Math.random() * 15;
      if (progress > 90) progress = 90;
      progressElement.style.width = `${progress}%`;
    }, 300);
    return interval;
  }

  protected stopProgress(progressElement: HTMLElement, interval: number | null): void {
    if (interval) {
      clearInterval(interval);
    }
    progressElement.style.width = '100%';
    setTimeout(() => {
      progressElement.style.width = '0';
    }, 300);
  }
}

export { Tool, ToolElements };
export type { ToolConfig };
